import type {Icon} from '@primer/octicons-react'

import {defaultFilterProviderOptions} from '../constants/defaults'
import type {FilterQuery} from '../filter-query'
import {
  type AnyBlock,
  BlockType,
  type FilterKey,
  type FilterProviderOptions,
  FilterProviderType,
  type FilterValueData,
  type FilterValueValidator,
  type IndexedBlockValueItem,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  type WithRequiredProperty,
} from '../types'
import {
  capitalize,
  caseInsensitiveStringCompare,
  matchesFilterValueOrAlias,
  shouldProviderShowSuggestions,
} from '../utils'

export class RootFilterProvider implements WithRequiredProperty<FilterKey, 'options'> {
  key: string
  aliases?: string[]
  displayName: string
  description?: string
  priority: number
  type: FilterProviderType
  icon: Icon
  options: FilterProviderOptions

  constructor(filterKey: FilterKey, options?: SuppliedFilterProviderOptions) {
    Object.assign(this, filterKey)
    this.key = filterKey.key
    this.icon = filterKey.icon
    this.type = FilterProviderType.Text
    this.displayName = filterKey.displayName ?? capitalize(filterKey.key)

    this.options = {
      ...defaultFilterProviderOptions,
      priority: filterKey.priority,
      ...options,
      filterTypes: {...defaultFilterProviderOptions.filterTypes, ...options?.filterTypes},
    }
    this.priority = this.options.priority
  }

  shouldGetSuggestions(filterBlock: AnyBlock | MutableFilterBlock): boolean {
    return (
      filterBlock &&
      filterBlock.type !== BlockType.Space &&
      shouldProviderShowSuggestions(filterBlock, this.options.filterTypes.multiValue)
    )
  }

  allowsFilterValues(): boolean {
    return this.options.filterTypes.inclusive !== false || this.options.filterTypes.exclusive !== false
  }

  /**
   * Default equality policy for matching a raw value against a `filterValues` entry. Honors the
   * provider's `caseSensitiveValues` option so providers with case-sensitive values (e.g. Git
   * branch names) match exactly, while token-like providers keep case-insensitive matching.
   */
  matchesFilterValue(filterValue: FilterValueData, value: string, aliasMatching = false): boolean {
    return matchesFilterValueOrAlias(filterValue, value, aliasMatching, this.options.caseSensitiveValues)
  }

  /**
   * Compares two raw filter values according to this provider's case-sensitivity policy. Use this
   * for all value equality checks inside providers (deduplication, suggestion lookup, etc.) so that
   * subclasses — including custom ones extending `AsyncFilterProvider` — consistently honor the
   * `caseSensitiveValues` option instead of hardcoding case-insensitive comparisons.
   */
  valuesEqual(a?: string, b?: string): boolean {
    return this.options.caseSensitiveValues ? !!a && !!b && a === b : caseInsensitiveStringCompare(a, b)
  }
}

export class ValueValidator<T = unknown> extends RootFilterProvider implements FilterValueValidator<T> {
  validateValue(
    filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    data: T | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    if (!data) return false

    return data
  }
}
